import { useState, useMemo } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ColumnDef } from '@tanstack/react-table'
import { Phone } from 'lucide-react'
import { DataTable, SortableHeader } from '@/components/ui/data-table'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useResolveProviderCallFlag, useTriggerProviderCallSync, useSyncCall } from '@/hooks/useAdmin'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { adminQueries } from '@/services/adminApi'
import { AdminCall, AdminProviderCall } from '@/services/adminApi'
import { CALL_STATUS_LABELS, getStatusBadgeVariant } from '@/types/account'
import { CurrencyDisplay } from '@/components/ui/currency-display'
import { NewAgentCallDialog } from '@/components/admin/NewAgentCallDialog'
import { useCurrency } from '@/hooks/useCurrency'
import { RefreshCw } from 'lucide-react'

// --- Sync Button Component ---
function SyncCallButton({ callId, status }: { callId: number; status: string }) {
  const syncCall = useSyncCall()
  const queryClient = useQueryClient()

  const isCompleted = status === 'completed'
  const isFailed = status === 'failed'

  if (isCompleted || isFailed) return null

  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8"
      disabled={syncCall.isPending}
      onClick={() => {
        syncCall.mutate(callId, {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'calls'] })
          }
        })
      }}
      title="Sync with provider"
    >
      <RefreshCw className={`h-4 w-4 ${syncCall.isPending ? 'animate-spin' : ''}`} />
    </Button>
  )
}

// --- Internal Calls columns ---
const callColumns: ColumnDef<AdminCall, any>[] = [
  {
    accessorKey: 'id',
    header: ({ column }) => <SortableHeader column={column} label="ID" />,
  },
  {
    id: 'user',
    header: 'User',
    cell: ({ row }) => {
      const accountId = row.original.accountId
      const accountEmail = row.original.accountEmail
      if (!accountId) return '-'
      return (
        <Link
          to="/admin/users/$accountId"
          params={{ accountId }}
          className="text-blue-600 hover:underline"
        >
          {accountEmail || accountId}
        </Link>
      )
    },
  },
  {
    accessorKey: 'externalId',
    header: ({ column }) => <SortableHeader column={column} label="External ID" />,
    cell: ({ row }) => {
      const externalId = row.original.externalId
      if (!externalId) return '-'
      return (
        <span className="text-xs font-mono text-muted-foreground">
          {externalId}
        </span>
      )
    },
  },
  {
    accessorKey: 'originNumber',
    header: ({ column }) => <SortableHeader column={column} label="Origin" />,
    cell: ({ row }) => row.original.originNumber || '-',
  },
  {
    accessorKey: 'destinationNumber',
    header: ({ column }) => <SortableHeader column={column} label="Destination" />,
  },
  {
    accessorKey: 'duration',
    header: ({ column }) => <SortableHeader column={column} label="Duration" />,
    cell: ({ row }) => {
      const seconds = row.original.duration
      if (seconds == null) return '-'
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${mins}:${String(secs).padStart(2, '0')}`
    },
  },
  {
    id: 'cost',
    header: 'Cost',
    cell: ({ row }) => <CallCostCell row={row.original} />,
  },
  {
    accessorKey: 'status',
    header: ({ column }) => <SortableHeader column={column} label="Status" />,
    cell: ({ row }) => {
      const status = row.original.status
      const label = CALL_STATUS_LABELS[status] || status

      if (row.original.isAgentCall && ['initiating', 'ringing', 'connected'].includes(status)) {
        const variantClasses: Record<string, string> = {
          initiating: 'bg-blue-600 animate-pulse',
          ringing: 'bg-yellow-600 animate-pulse',
          connected: 'bg-green-600 animate-pulse',
        }
        return (
          <Badge className={variantClasses[status] || ''}>
            <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 bg-white" />
            {label}
          </Badge>
        )
      }

      return <Badge variant={getStatusBadgeVariant(status)}>{label}</Badge>
    },
  },
  {
    id: 'type',
    header: 'Type',
    cell: ({ row }) => {
      if (row.original.isAgentCall) {
        return <Badge variant="outline">Agent</Badge>
      }
      return <Badge variant="secondary">User</Badge>
    },
  },
  {
    accessorKey: 'startTime',
    header: ({ column }) => <SortableHeader column={column} label="Date" />,
    cell: ({ row }) => {
      const startTime = row.original.startTime
      if (!startTime) return '-'
      return new Date(startTime).toLocaleString()
    },
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => (
      <SyncCallButton callId={row.original.id} status={row.original.status} />
    ),
  },
]

// --- Cost cell component (uses context currency for agent calls) ---
function CallCostCell({ row }: { row: AdminCall }) {
  const { currency } = useCurrency()
  const cost = row.cost

  if (row.isAgentCall) {
    const agentCost = row.agentCostCents || 0
    if (agentCost <= 0) return <>-</>
    return <CurrencyDisplay amount={agentCost} currency={currency.code} debit hideSign hideColor inCents />
  }

  if (!cost) return <>-</>
  return <CurrencyDisplay amount={cost.amount} currency={cost.currency} debit hideSign hideColor inCents />
}

// --- Flag helpers ---
const FLAG_LABELS: Record<string, string> = {
  unmatched_provider: 'Unmatched (Provider)',
  unmatched_internal: 'Unmatched (Internal)',
  price_mismatch: 'Price Mismatch',
  rate_deviation: 'Rate Deviation',
}

const FLAG_VARIANTS: Record<string, 'destructive' | 'secondary' | 'default'> = {
  unmatched_provider: 'destructive',
  unmatched_internal: 'destructive',
  price_mismatch: 'destructive',
  rate_deviation: 'secondary',
}

function formatDuration(seconds: number | null) {
  if (seconds == null) return '-'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${String(secs).padStart(2, '0')}`
}

function PriceDisplay({ price, unit }: { price: number | null; unit: string | null }) {
  const { currency } = useCurrency()
  if (price == null) return <>-</>
  return <CurrencyDisplay amount={price} currency={unit || currency.code} debit hideSign hideColor decimalPlaces={4} />
}

function FlagBadge({ flag, resolved }: { flag: string | null; resolved: boolean }) {
  if (!flag) return <span className="text-muted-foreground">-</span>
  if (resolved) return <Badge variant="default">Resolved</Badge>
  return (
    <Badge variant={FLAG_VARIANTS[flag] || 'secondary'}>
      {FLAG_LABELS[flag] || flag}
    </Badge>
  )
}

function SidLabel({ sid }: { sid: string }) {
  return (
    <span className="text-xs font-mono text-muted-foreground" title={sid}>
      {sid.substring(0, 16)}...
    </span>
  )
}


// --- Call group: parent + child legs ---
interface CallGroup {
  parentSid: string
  legs: AdminProviderCall[]
  totalPrice: number
  callId: string | null
}

/** Groups provider calls by parent SID. A call with no parentSid is its own parent. */
function groupByParent(calls: AdminProviderCall[]): CallGroup[] {
  const parentMap = new Map<string, AdminProviderCall[]>()

  for (const pc of calls) {
    // The grouping key: if a call has a parentSid, group under the parent.
    // If it IS the parent (no parentSid), group under its own providerSid.
    const key = pc.parentSid || pc.providerSid
    const existing = parentMap.get(key) || []
    existing.push(pc)
    parentMap.set(key, existing)
  }

  const groups: CallGroup[] = []
  for (const [parentSid, legs] of parentMap) {
    // Sort: parent leg first (the one whose providerSid === parentSid), then children
    legs.sort((a, b) => {
      if (a.providerSid === parentSid) return -1
      if (b.providerSid === parentSid) return 1
      return 0
    })

    const totalPrice = legs.reduce((sum, l) => sum + Math.abs(l.price ?? 0), 0)
    const callId = legs.find(l => l.callId)?.callId ?? null

    groups.push({ parentSid, legs, totalPrice, callId })
  }

  // Sort by most recent first
  groups.sort((a, b) => {
    const aTime = a.legs[0]?.startTime ?? ''
    const bTime = b.legs[0]?.startTime ?? ''
    return bTime.localeCompare(aTime)
  })

  return groups
}

// --- Flat provider calls columns (used in Flagged tab) ---
const providerCallColumns: ColumnDef<AdminProviderCall, any>[] = [
  {
    accessorKey: 'provider',
    header: ({ column }) => <SortableHeader column={column} label="Provider" />,
    cell: ({ row }) => (
      <Badge variant="secondary">{row.original.provider}</Badge>
    ),
  },
  {
    accessorKey: 'providerSid',
    header: ({ column }) => <SortableHeader column={column} label="SID" />,
    cell: ({ row }) => <SidLabel sid={row.original.providerSid} />,
  },
  {
    accessorKey: 'fromNumber',
    header: ({ column }) => <SortableHeader column={column} label="From" />,
    cell: ({ row }) => row.original.fromNumber || '-',
  },
  {
    accessorKey: 'toNumber',
    header: ({ column }) => <SortableHeader column={column} label="To" />,
    cell: ({ row }) => row.original.toNumber || '-',
  },
  {
    accessorKey: 'durationSeconds',
    header: ({ column }) => <SortableHeader column={column} label="Duration" />,
    cell: ({ row }) => formatDuration(row.original.durationSeconds),
  },
  {
    id: 'price',
    header: 'Price',
    cell: ({ row }) => <PriceDisplay price={row.original.price} unit={row.original.priceUnit} />,
  },
  {
    id: 'flag',
    header: 'Flag',
    cell: ({ row }) => <FlagBadge flag={row.original.flag} resolved={row.original.flagResolved} />,
  },
  {
    id: 'matched',
    header: 'Matched',
    cell: ({ row }) => row.original.callId
      ? <span className="text-xs font-mono text-green-600">{row.original.callId.substring(0, 8)}...</span>
      : <span className="text-muted-foreground">-</span>,
  },
  {
    accessorKey: 'startTime',
    header: ({ column }) => <SortableHeader column={column} label="Date" />,
    cell: ({ row }) => {
      const t = row.original.startTime
      if (!t) return '-'
      return new Date(t).toLocaleString()
    },
  },
]

export const Route = createFileRoute('/admin/calls')({
  component: AdminCallsPage,
})

function AdminCallsPage() {
  const [showNewCallDialog, setShowNewCallDialog] = useState(false)
  const navigate = useNavigate()

  const handleCallInitiated = (callId: string) => {
    navigate({ to: '/admin/agent-console/$callId', params: { callId } })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Calls</h1>
        <Button onClick={() => setShowNewCallDialog(true)}>
          <Phone className="h-4 w-4 mr-2" />
          New Agent Call
        </Button>
      </div>

      <Tabs defaultValue="calls">
        <TabsList>
          <TabsTrigger value="calls">Calls</TabsTrigger>
          <TabsTrigger value="provider">Provider Calls</TabsTrigger>
          <TabsTrigger value="flagged">Flagged</TabsTrigger>
        </TabsList>
        <TabsContent value="calls">
          <InternalCallsTab />
        </TabsContent>
        <TabsContent value="provider">
          <ProviderCallsTab />
        </TabsContent>
        <TabsContent value="flagged">
          <FlaggedCallsTab />
        </TabsContent>
      </Tabs>

      <NewAgentCallDialog
        open={showNewCallDialog}
        onOpenChange={setShowNewCallDialog}
        onCallInitiated={handleCallInitiated}
      />
    </div>
  )
}

function InternalCallsTab() {
  const [page, setPage] = useState(1)
  const [callType, setCallType] = useState<'all' | 'user' | 'agent'>('all')
  const pageSize = 20
  const { data, isLoading } = useQuery(adminQueries.calls(page, pageSize))

  const filteredCalls = useMemo(() => {
    if (!data?.calls) return []
    if (callType === 'all') return data.calls
    if (callType === 'agent') return data.calls.filter(c => c.isAgentCall)
    return data.calls.filter(c => !c.isAgentCall)
  }, [data?.calls, callType])

  return (
    <Card flush className="overflow-hidden">
      <CardHeader>
        <CardTitle>Calls</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-96 w-full" />
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <select
                value={callType}
                onChange={(e) => { setCallType(e.target.value as 'all' | 'user' | 'agent'); setPage(1) }}
                className="px-3 py-2 border rounded-md bg-background text-sm"
              >
                <option value="all">All Calls</option>
                <option value="user">User Calls</option>
                <option value="agent">Agent Calls</option>
              </select>
            </div>
            <DataTable
              columns={callColumns}
              data={filteredCalls}
              enableSearch={false}
              manualPagination={{
                pageIndex: page,
                pageSize,
                totalItems: data?.totalItems ?? 0,
                onPageChange: setPage,
                isLoading,
              }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function ProviderCallsTab() {
  const [page, setPage] = useState(1)
  const [provider, setProvider] = useState('')
  const [flag, setFlag] = useState('')
  const [syncStart, setSyncStart] = useState('')
  const [syncEnd, setSyncEnd] = useState('')
  const pageSize = 40 // Fetch more since we group them

  const filters = {
    provider: provider || undefined,
    flag: flag || undefined,
  }
  const { data, isLoading } = useQuery(adminQueries.providerCalls(page, pageSize, filters))
  const { data: summary } = useQuery(adminQueries.providerCallSyncSummary())
  const triggerSync = useTriggerProviderCallSync()

  const groups = useMemo(
    () => groupByParent(data?.providerCalls ?? []),
    [data?.providerCalls]
  )

  const handleSync = () => {
    if (!syncStart || !syncEnd) return
    triggerSync.mutate({ startDate: syncStart, endDate: syncEnd })
  }

  return (
    <Card flush className="overflow-hidden">
      <CardHeader>
        <CardTitle>Provider Calls</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {summary && (
            <div className="flex gap-4 text-sm text-muted-foreground flex-wrap">
              <span>Total: {summary.total}</span>
              <span>Matched: {summary.matched}</span>
              <span>Unresolved: {summary.unresolved}</span>
              {summary.lastSync && (
                <span>Last sync: {new Date(summary.lastSync).toLocaleString()}</span>
              )}
            </div>
          )}

          <div className="flex gap-4 items-center flex-wrap">
            <select
              value={provider}
              onChange={(e) => { setProvider(e.target.value); setPage(1) }}
              className="px-3 py-2 border rounded-md bg-background text-sm"
            >
              <option value="">All Providers</option>
              <option value="twilio">Twilio</option>
              <option value="telnyx">Telnyx</option>
            </select>
            <select
              value={flag}
              onChange={(e) => { setFlag(e.target.value); setPage(1) }}
              className="px-3 py-2 border rounded-md bg-background text-sm"
            >
              <option value="">All Flags</option>
              <option value="any">Any Flag</option>
              <option value="unmatched_provider">Unmatched (Provider)</option>
              <option value="unmatched_internal">Unmatched (Internal)</option>
              <option value="price_mismatch">Price Mismatch</option>
              <option value="rate_deviation">Rate Deviation</option>
            </select>
            {(provider || flag) && (
              <Button variant="outline" size="sm" onClick={() => { setProvider(''); setFlag(''); setPage(1) }}>
                Clear
              </Button>
            )}
            <div className="ml-auto flex gap-2 items-center flex-wrap">
              <input
                type="date"
                value={syncStart}
                onChange={(e) => setSyncStart(e.target.value)}
                className="px-3 py-2 border rounded-md bg-background text-sm"
              />
              <input
                type="date"
                value={syncEnd}
                onChange={(e) => setSyncEnd(e.target.value)}
                className="px-3 py-2 border rounded-md bg-background text-sm"
              />
              <Button
                size="sm"
                disabled={!syncStart || !syncEnd || triggerSync.isPending}
                onClick={handleSync}
              >
                {triggerSync.isPending ? 'Syncing...' : 'Sync Range'}
              </Button>
            </div>
          </div>

          {isLoading ? (
            <Skeleton className="h-96 w-full" />
          ) : (
            <GroupedProviderCallsTable
              groups={groups}
              page={page}
              pageSize={pageSize}
              totalItems={data?.totalItems ?? 0}
              onPageChange={setPage}
            />
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function GroupedProviderCallsTable({
  groups,
  page,
  pageSize,
  totalItems,
  onPageChange,
}: {
  groups: CallGroup[]
  page: number
  pageSize: number
  totalItems: number
  onPageChange: (page: number) => void
}) {
  const totalPages = Math.ceil(totalItems / pageSize)

  if (groups.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No provider calls found
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="rounded-md border max-sm:-mx-6 max-sm:rounded-none max-sm:border-x-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-3 py-2 text-left font-medium">Leg</th>
              <th className="px-3 py-2 text-left font-medium">Provider</th>
              <th className="px-3 py-2 text-left font-medium">SID</th>
              <th className="px-3 py-2 text-left font-medium">From</th>
              <th className="px-3 py-2 text-left font-medium">To</th>
              <th className="px-3 py-2 text-left font-medium">Duration</th>
              <th className="px-3 py-2 text-right font-medium">Price</th>
              <th className="px-3 py-2 text-left font-medium">Flag</th>
              <th className="px-3 py-2 text-left font-medium">Matched</th>
              <th className="px-3 py-2 text-left font-medium">Date</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <CallGroupRows key={group.parentSid} group={group} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-2">
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages} ({totalItems} records)
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function CallGroupRows({ group }: { group: CallGroup }) {
  const isMultiLeg = group.legs.length > 1
  const firstLeg = group.legs[0]

  if (!isMultiLeg) {
    // Single-leg call: one normal row
    const leg = firstLeg
    return (
      <tr className="border-b">
        <td className="px-3 py-2 whitespace-nowrap">
          <span className="text-xs text-muted-foreground">1 leg</span>
        </td>
        <td className="px-3 py-2"><Badge variant="secondary">{leg.provider}</Badge></td>
        <td className="px-3 py-2"><SidLabel sid={leg.providerSid} /></td>
        <td className="px-3 py-2">{leg.fromNumber || '-'}</td>
        <td className="px-3 py-2">{leg.toNumber || '-'}</td>
        <td className="px-3 py-2">{formatDuration(leg.durationSeconds)}</td>
        <td className="px-3 py-2 text-right tabular-nums"><PriceDisplay price={leg.price} unit={leg.priceUnit} /></td>
        <td className="px-3 py-2"><FlagBadge flag={leg.flag} resolved={leg.flagResolved} /></td>
        <td className="px-3 py-2">
          {leg.callId
            ? <span className="text-xs font-mono text-green-600">{leg.callId.substring(0, 8)}...</span>
            : <span className="text-muted-foreground">-</span>}
        </td>
        <td className="px-3 py-2">{leg.startTime ? new Date(leg.startTime).toLocaleString() : '-'}</td>
      </tr>
    )
  }

  // Multi-leg: summary header row + individual leg rows
  const priceUnit = firstLeg.priceUnit
  const flags = group.legs.filter(l => l.flag && !l.flagResolved)
  const worstFlag = flags.length > 0 ? flags[0].flag : null

  return (
    <>
      {/* Summary row */}
      <tr className="border-b-0 bg-muted/40">
        <td className="px-3 py-2 whitespace-nowrap" colSpan={2}>
          <span className="text-xs font-medium">{group.legs.length} legs</span>
        </td>
        <td className="px-3 py-2"><SidLabel sid={group.parentSid} /></td>
        <td className="px-3 py-2" colSpan={2}></td>
        <td className="px-3 py-2">{formatDuration(firstLeg.durationSeconds)}</td>
        <td className="px-3 py-2 text-right tabular-nums font-medium"><PriceDisplay price={group.totalPrice} unit={priceUnit} /></td>
        <td className="px-3 py-2"><FlagBadge flag={worstFlag} resolved={false} /></td>
        <td className="px-3 py-2">
          {group.callId
            ? <span className="text-xs font-mono text-green-600">{group.callId.substring(0, 8)}...</span>
            : <span className="text-muted-foreground">-</span>}
        </td>
        <td className="px-3 py-2">{firstLeg.startTime ? new Date(firstLeg.startTime).toLocaleString() : '-'}</td>
      </tr>
      {/* Individual legs */}
      {group.legs.map((leg, index) => (
        <tr key={leg.id} className={`bg-muted/20 ${index === group.legs.length - 1 ? 'border-b-2' : 'border-b border-dashed'}`}>
          <td className="px-3 py-1.5 whitespace-nowrap">
            <span className="text-xs text-muted-foreground pl-3">{'\u2514'} leg {index + 1}</span>
          </td>
          <td className="px-3 py-1.5"><Badge variant="secondary">{leg.provider}</Badge></td>
          <td className="px-3 py-1.5"><SidLabel sid={leg.providerSid} /></td>
          <td className="px-3 py-1.5 text-xs text-muted-foreground">{leg.fromNumber || '-'}</td>
          <td className="px-3 py-1.5 text-xs text-muted-foreground">{leg.toNumber || '-'}</td>
          <td className="px-3 py-1.5 text-xs text-muted-foreground">{formatDuration(leg.durationSeconds)}</td>
          <td className="px-3 py-1.5 text-right tabular-nums text-xs text-muted-foreground"><PriceDisplay price={leg.price} unit={leg.priceUnit} /></td>
          <td className="px-3 py-1.5"><FlagBadge flag={leg.flag} resolved={leg.flagResolved} /></td>
          <td className="px-3 py-1.5">
            {leg.callId
              ? <span className="text-xs font-mono text-green-600">{leg.callId.substring(0, 8)}...</span>
              : <span className="text-muted-foreground">-</span>}
          </td>
          <td className="px-3 py-1.5 text-xs text-muted-foreground">{leg.startTime ? new Date(leg.startTime).toLocaleString() : '-'}</td>
        </tr>
      ))}
    </>
  )
}

function FlaggedCallsTab() {
  const [page, setPage] = useState(1)
  const pageSize = 20
  const { data, isLoading } = useProviderCalls(page, pageSize, { flag: 'unresolved' })
  const resolveFlag = useResolveProviderCallFlag()

  const flaggedColumns: ColumnDef<AdminProviderCall, any>[] = [
    ...providerCallColumns,
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        if (row.original.flagResolved) return null
        return (
          <Button
            size="sm"
            variant="outline"
            disabled={resolveFlag.isPending}
            onClick={() => resolveFlag.mutate({ id: row.original.id })}
          >
            Resolve
          </Button>
        )
      },
    },
  ]

  const calls = data?.providerCalls ?? []

  return (
    <Card flush className="overflow-hidden">
      <CardHeader>
        <CardTitle>Flagged Calls</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-96 w-full" />
        ) : calls.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No unresolved flagged calls
          </div>
        ) : (
          <DataTable
            columns={flaggedColumns}
            data={calls}
            enableSearch={false}
            manualPagination={{
              pageIndex: page,
              pageSize,
              totalItems: data?.totalItems ?? 0,
              onPageChange: setPage,
              isLoading,
            }}
          />
        )}
      </CardContent>
    </Card>
  )
}
